/**
 * One contract by its EXACT number, as a contacts-store row ({ c, n, p, i, b, d, e, s, t }).
 *
 * Looked up in the contacts store first (in memory), then by document id in the two contract
 * collections (airport_contracts, contracts) - two reads, never a search: a mistyped number
 * (one digit missing) must say "not found", never land on another contract. Nothing is ever
 * invented: the status is the record's own ('' when the record has none).
 */
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { contactsService, contractKey, phoneDigits, isOpenStatus } from './contactsService';
import { USE_CONTRACTS_V2, v2RecordsByIds, contactRowOfRecord, isAirportRecord } from './contractsV2';

/** Open = Ejar's "Open" and nothing else: an empty / unknown status is NOT open. */
export { isOpenStatus };

export { contractNotFoundText, contractUncheckedText } from './contractText';

const pick = (d, ...keys) => {
    for (const k of keys) {
        const v = d && d[k];
        if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
};

/** A contract document (either collection's field names) as a contacts-store row; `id` is the
 *  document id = the exact contract number it was read by. */
export const rowFromContractDoc = (id, d) => ({
    c: contractKey(id || pick(d, 'Contract No.', 'contract_no')),
    n: pick(d, 'Customer', 'customer', 'customer_name'),
    p: phoneDigits(pick(d, 'Phone Number', 'phone', 'phone_number')),
    i: pick(d, 'Customer ID', 'identity', 'identity_number'),
    b: pick(d, 'Pick-up Branch', 'pickup_branch'),
    d: pick(d, 'Pick-up Date', 'pickup_date'),
    e: pick(d, 'Drop-off Date', 'dropoff_date'),
    s: pick(d, 'Status', 'status'),
    t: pick(d, 'Customer Type', 'customer_type'),
});

const readContractDoc = async (collectionName, id) => {
    const snap = await getDoc(doc(db, collectionName, id));
    return snap.exists() ? snap.data() : null;
};

/** The store's record of this exact number (USE_CONTRACTS_V2): { row, source } or null; a failed read throws. */
async function findInStore(id, byIds = v2RecordsByIds) {
    const rec = (await byIds([id], 'all')).find((v) => contractKey(v.contract_no) === id);
    return rec ? { row: contactRowOfRecord(rec), source: isAirportRecord(rec) ? 'airport' : 'contracts' } : null;
}

/**
 * The contract document of this exact number, or null when neither collection has it.
 * Throws when the reads failed (a failed read is not "no such contract").
 * `read(collection, id)` is injectable for tests. With USE_CONTRACTS_V2 (and no `read`) the contracts store is
 * asked by the exact number instead (`byIds` injectable for tests).
 */
export async function findContractRecord(contractNo, { read, byIds, store = USE_CONTRACTS_V2 } = {}) {
    const id = contractKey(contractNo);
    if (!id || id.includes('/')) return null;
    if (store && !read) return findInStore(id, byIds);
    const reader = read || readContractDoc;
    const results = await Promise.allSettled([reader('airport_contracts', id), reader('contracts', id)]);
    const sources = ['airport', 'contracts'];
    for (let i = 0; i < results.length; i += 1) {
        if (results[i].status === 'fulfilled' && results[i].value) {
            return { row: rowFromContractDoc(id, results[i].value), source: sources[i] };
        }
    }
    const failed = results.find((r) => r.status === 'rejected');
    if (failed) throw failed.reason;
    return null;
}

/**
 * The contract of this exact number: the contacts-store row, else the contract document.
 * { row, source: 'contacts' | 'airport' | 'contracts' } or null. Throws when it could not be checked.
 */
export async function findContract(contractNo, { store = contactsService, read } = {}) {
    const inStore = store.peek(contractNo);
    if (inStore) return { row: inStore, source: 'contacts' };
    return findContractRecord(contractNo, read ? { read } : undefined);
}
